/**
 * المرحلة 1.35 — one panel of the CDC growth chart, birth to 36 months, as the
 * centre's blue sheet prints it: the 5th to 95th percentile lines for the
 * child's sex, the band between the 5th and the 95th, and the child's own
 * readings joined, the last one with its percentile.
 *
 * The lines are drawn from the CDC's L, M and S (domain/growthData.js), so a
 * reading sits exactly where the paper would put it. Age runs left to right
 * whatever the page's direction, as on the paper: the SVG's own `direction`,
 * since `dir` is an HTML attribute and an inline SVG inherits the page's RTL.
 */

import { useMemo } from 'react';
import { MAX_MONTHS, percentileOf, sexCode } from '../../domain/growthData';
import { chartRange, percentileLines, percentileTone } from '../../domain/childFile';
import { useI18n } from '../../i18n/I18nProvider';

const W = 720;
const H = 400;
const PAD = { top: 34, right: 42, bottom: 34, left: 46 };

/** Labels at the lines' right end, pushed apart where the lines run close. */
function spread(labels, gap = 11) {
  const sorted = [...labels].sort((a, b) => a.y - b.y);
  for (let i = 1; i < sorted.length; i += 1) {
    if (sorted[i].y - sorted[i - 1].y < gap) sorted[i].y = sorted[i - 1].y + gap;
  }
  return sorted;
}

/**
 * @param {{ chart: 'length'|'weight'|'head', sex: 'male'|'female', points: { ageMonths: number, value: number }[], compact?: boolean }} props
 */
export function GrowthChart({ chart, sex, points = [], compact = false }) {
  const { t } = useI18n();
  const s = sexCode(sex) || 1;
  const range = useMemo(() => chartRange(chart, s, points), [chart, s, points]);
  const lines = useMemo(() => percentileLines(chart, s), [chart, s]);

  const x = (age) => PAD.left + (age / MAX_MONTHS) * (W - PAD.left - PAD.right);
  const y = (v) => H - PAD.bottom - ((v - range.min) / (range.max - range.min)) * (H - PAD.top - PAD.bottom);
  const path = (pts) => pts.map(([a, v], i) => `${i ? 'L' : 'M'}${x(a).toFixed(1)},${y(v).toFixed(1)}`).join(' ');

  const p5 = lines.find((l) => l.p === 5).points;
  const p95 = lines.find((l) => l.p === 95).points;
  const band = `${path(p5)} ${[...p95]
    .reverse()
    .map(([a, v]) => `L${x(a).toFixed(1)},${y(v).toFixed(1)}`)
    .join(' ')} Z`;

  const labels = spread(lines.map((l) => ({ p: l.p, y: y(l.points[l.points.length - 1][1]) })));
  const ticksY = [];
  for (let v = range.min; v <= range.max + 1e-9; v += range.step) ticksY.push(Math.round(v * 10) / 10);
  const sorted = [...points].sort((a, b) => a.ageMonths - b.ageMonths);
  const last = sorted[sorted.length - 1];
  const lastP = last ? percentileOf(chart, s, last.ageMonths, last.value) : null;
  const unit = t(`childFile.unit.${chart === 'weight' ? 'kg' : 'cm'}`);

  return (
    <figure className={`cf-chart cf-chart--${s === 2 ? 'girl' : 'boy'}${compact ? ' cf-chart--compact' : ''}`} data-testid={`growth-chart-${chart}`}>
      <svg viewBox={`0 0 ${W} ${H}`} direction="ltr" style={{ direction: 'ltr' }} role="img" aria-label={t(`childFile.chart.${chart}`)}>
        <text className="cf-chart__title" x={PAD.left} y={20}>
          {t(`childFile.chart.${chart}`)} — {t(s === 2 ? 'childFile.chart.girls' : 'childFile.chart.boys')}
        </text>
        <text className="cf-chart__unit" x={W - PAD.right} y={20} textAnchor="end">
          {unit}
        </text>

        {/* The grid: every month, stronger every three, as the paper's. */}
        {Array.from({ length: MAX_MONTHS + 1 }, (_, m) => (
          <line key={`gx${m}`} className={m % 3 ? 'cf-chart__grid' : 'cf-chart__grid cf-chart__grid--major'} x1={x(m)} x2={x(m)} y1={PAD.top} y2={H - PAD.bottom} />
        ))}
        {ticksY.map((v) => (
          <g key={`gy${v}`}>
            <line className="cf-chart__grid cf-chart__grid--major" x1={PAD.left} x2={W - PAD.right} y1={y(v)} y2={y(v)} />
            <text className="cf-chart__tick" x={PAD.left - 6} y={y(v) + 3.5} textAnchor="end">
              {v}
            </text>
          </g>
        ))}
        {Array.from({ length: MAX_MONTHS / 3 + 1 }, (_, i) => i * 3).map((m) => (
          <text key={`tx${m}`} className="cf-chart__tick" x={x(m)} y={H - PAD.bottom + 15} textAnchor="middle">
            {m === 0 ? t('childFile.chart.birth') : m}
          </text>
        ))}
        <text className="cf-chart__axis" x={(W + PAD.left - PAD.right) / 2} y={H - 4} textAnchor="middle">
          {t('childFile.chart.ageMonths')}
        </text>

        {/* The 5th–95th band and the seven printed lines. */}
        <path className="cf-chart__band" d={band} />
        {lines.map((l) => (
          <path key={l.p} className={l.p === 50 ? 'cf-chart__line cf-chart__line--median' : 'cf-chart__line'} d={path(l.points)} />
        ))}
        {labels.map((l) => (
          <text key={`l${l.p}`} className="cf-chart__label" x={W - PAD.right + 5} y={l.y + 3.5}>
            {l.p}
          </text>
        ))}

        {/* The child's readings, joined. */}
        {sorted.length > 1 && <path className="cf-chart__child" d={path(sorted.map((p) => [p.ageMonths, p.value]))} />}
        {sorted.map((p, i) => (
          <circle
            key={`${p.ageMonths}-${i}`}
            className={i === sorted.length - 1 ? 'cf-chart__dot cf-chart__dot--last' : 'cf-chart__dot'}
            cx={x(p.ageMonths)}
            cy={y(p.value)}
            r={i === sorted.length - 1 ? 6 : 4}
          >
            <title>{`${p.value} ${unit} · ${p.ageMonths} ${t('childFile.chart.monthShort')}`}</title>
          </circle>
        ))}
        {last && lastP !== null && (
          <g
            className={`cf-chart__callout cf-chart__callout--${percentileTone(lastP)}`}
            transform={`translate(${Math.min(x(last.ageMonths) + 10, W - PAD.right - 92)},${Math.max(y(last.value) - 30, PAD.top + 2)})`}
          >
            <rect width="88" height="22" rx="11" />
            <text x="44" y="15" textAnchor="middle">
              {t('childFile.chart.percentileShort', { p: Math.round(lastP) })}
            </text>
          </g>
        )}
      </svg>
      {!points.length && <figcaption className="cf-chart__empty">{t('childFile.chart.noPoints')}</figcaption>}
    </figure>
  );
}

export default GrowthChart;
